import { h } from 'preact';
import { SelectEntry } from '../entries/SelectEntry';
import { TextFieldEntry } from '../entries/TextFieldEntry';
import { CheckboxEntry } from '../entries/CheckboxEntry';

const getAllTriggerTypes = (i18n) => [
  { value: '', label: i18n['opt.none'] || '-- None --' },
  { value: 'Timer', label: i18n['opt.Timer'] || 'Timer' },
  { value: 'Message', label: i18n['opt.Message'] || 'Message' },
  { value: 'Conditional', label: i18n['opt.Conditional'] || 'Conditional' },
  { value: 'Signal', label: i18n['opt.Signal'] || 'Signal' },
  { value: 'Error', label: i18n['opt.Error'] || 'Error' },
  { value: 'Escalation', label: i18n['opt.Escalation'] || 'Escalation' },
  { value: 'Compensation', label: i18n['opt.Compensation'] || 'Compensation' },
  { value: 'Cancel', label: i18n['opt.Cancel'] || 'Cancel' },
  { value: 'Link', label: i18n['opt.Link'] || 'Link' },
  { value: 'Terminate', label: i18n['opt.Terminate'] || 'Terminate' },
  { value: 'Multiple', label: i18n['opt.Multiple'] || 'Multiple' },
  { value: 'ParallelMultiple', label: i18n['opt.ParallelMultiple'] || 'Parallel Multiple' },
];

// Valid triggers per event type (BPMN 2.0 spec)
const TRIGGERS_BY_TYPE = {
  'epx:StartEvent': ['', 'Timer', 'Message', 'Signal', 'Conditional', 'Error', 'Escalation', 'Compensation', 'Multiple', 'ParallelMultiple'],
  'epx:EndEvent': ['', 'Message', 'Signal', 'Error', 'Escalation', 'Compensation', 'Cancel', 'Terminate', 'Multiple'],
  'epx:IntermediateEvent': ['', 'Timer', 'Message', 'Signal', 'Conditional', 'Link', 'Compensation', 'Multiple', 'ParallelMultiple'],
  'epx:IntermediateThrowEvent': ['', 'Message', 'Signal', 'Link', 'Compensation', 'Escalation'],
  'epx:BoundaryEvent': ['', 'Timer', 'Message', 'Signal', 'Conditional', 'Error', 'Escalation', 'Compensation', 'Cancel', 'Multiple', 'ParallelMultiple'],
};

const APPLICABLE_TYPES = [
  'epx:StartEvent',
  'epx:EndEvent',
  'epx:IntermediateEvent',
  'epx:IntermediateThrowEvent',
  'epx:BoundaryEvent',
];

// Types that support non-interrupting mode
const INTERRUPTING_TYPES = ['epx:StartEvent', 'epx:BoundaryEvent'];

function getTriggersForType(elementType, i18n) {
  const allowed = TRIGGERS_BY_TYPE[elementType];
  const all = getAllTriggerTypes(i18n);
  if (!allowed) return all;
  return all.filter(t => allowed.includes(t.value));
}

export class TriggerPropertiesProvider {
  constructor(lf, i18n) {
    this._lf = lf;
    this._i18n = i18n;
  }

  isApplicable(elementType) {
    return APPLICABLE_TYPES.includes(elementType);
  }

  getGroups(element) {
    const { lf, i18n } = { lf: this._lf, i18n: this._i18n };
    const props = element.properties || {};
    const id = element.id;
    const elementType = element.type;

    const update = (key, value) => {
      lf.setProperties(id, { [key]: value });
      lf.emit('node:properties-change', { id, properties: { [key]: value } });
    };

    const entries = [
      {
        id: 'triggerType',
        component: () => (
          <SelectEntry
            id="epx-trigger"
            label={i18n.triggerType || 'Trigger Type'}
            value={props.trigger || ''}
            options={getTriggersForType(elementType, i18n)}
            onChange={(val) => update('trigger', val)}
          />
        ),
      },
      {
        id: 'triggerExpression',
        component: () => (
          <TextFieldEntry
            id="epx-trigger-expression"
            label={i18n.expression || 'Expression'}
            value={props.expression || ''}
            onChange={(val) => update('expression', val)}
          />
        ),
      },
    ];

    // Add isInterrupting checkbox for StartEvent and BoundaryEvent
    if (INTERRUPTING_TYPES.includes(elementType)) {
      entries.push({
        id: 'isInterrupting',
        component: () => (
          <CheckboxEntry
            id="epx-is-interrupting"
            label={i18n.isInterrupting || 'Interrupting'}
            checked={props.isInterrupting !== false}
            onChange={(val) => update('isInterrupting', val)}
          />
        ),
      });
    }

    return [
      {
        id: 'trigger',
        label: i18n.trigger || 'Trigger',
        entries,
      },
    ];
  }
}
